import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { C, formatDate } from '../../../../../lib/design';
import { Ic, ICON_PATHS, StatusBadge, DocTypeBadge } from '../../../../../lib/ui';
import { fetchDocumentos, obtenerUrlArchivo } from '../../../../shared/services/api';
import { useI18n } from '../../../../../i18n';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';
import type { DocumentoResumen } from '../../../../../types';

type TipologiaFilter = 'PE' | 'PO' | 'PS' | '';
type SortKey = 'codigo' | 'titulo' | 'tipoDocumental' | 'estado' | 'actualizadoEn';

const ACCENT = '#0E243E';
const PAGE_SIZE = 20;

// --- Stats Card ---
function StatCard({ value, label, color }: { value: number; label: string; color: string }) {
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e8e8e8',
        borderRadius: 8,
        padding: '16px 20px',
        minWidth: 0,
      }}
    >
      <div style={{ fontSize: 24, fontWeight: 800, color, lineHeight: 1.2 }}>{value}</div>
      <div style={{ fontSize: 13, color: 'rgba(0,0,0,0.54)', marginTop: 4 }}>{label}</div>
    </div>
  );
}

// --- Pagination ---
function Pagination({
  current,
  total,
  onChange,
}: {
  current: number;
  total: number;
  onChange: (p: number) => void;
}) {
  if (total <= 1) return null;
  const pages: number[] = [];
  for (let i = 1; i <= total; i++) pages.push(i);

  const btnBase: React.CSSProperties = {
    padding: '6px 12px',
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'inherit',
    borderRadius: 6,
    cursor: 'pointer',
    border: '1px solid #d5d5d5',
    background: 'transparent',
    color: ACCENT,
    transition: 'all 0.15s',
    minWidth: 36,
    textAlign: 'center',
  };
  const btnActive: React.CSSProperties = {
    ...btnBase,
    background: ACCENT,
    color: '#fff',
    border: `1px solid ${ACCENT}`,
  };
  const btnDisabled: React.CSSProperties = {
    ...btnBase,
    opacity: 0.35,
    cursor: 'default',
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 6, padding: '16px 0' }}>
      <button
        style={current === 1 ? btnDisabled : btnBase}
        onClick={() => current > 1 && onChange(current - 1)}
        disabled={current === 1}
      >
        {'\u2190'}
      </button>
      {pages.map((p) => (
        <button key={p} style={p === current ? btnActive : btnBase} onClick={() => onChange(p)}>
          {p}
        </button>
      ))}
      <button
        style={current === total ? btnDisabled : btnBase}
        onClick={() => current < total && onChange(current + 1)}
        disabled={current === total}
      >
        {'\u2192'}
      </button>
    </div>
  );
}

// --- Main ---
export function DocumentExplorerPanel() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [docs, setDocs] = useState<DocumentoResumen[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtroTipologia, setFiltroTipologia] = useState<TipologiaFilter>('');
  const [filtroEstado, setFiltroEstado] = useState<string>('');
  const [filtroProceso, setFiltroProceso] = useState<string>('');
  const [filtroTipoContenido, setFiltroTipoContenido] = useState<string>('');
  const [busqueda, setBusqueda] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortKey, setSortKey] = useState<SortKey>('codigo');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchDocumentos({ porPagina: '100', excluirTipos: 'FP,FR,FG' })
      .then((r) => setDocs(r.datos ?? []))
      .catch((e) => setError(e?.message ?? 'Error al cargar documentos'))
      .finally(() => setLoading(false));
  }, []);

  const stats = useMemo(
    () => ({
      total: docs.length,
      vigentes: docs.filter((d) => d.estado === 'PUBLICADO').length,
      borrador: docs.filter((d) => d.estado === 'BORRADOR').length,
      revision: docs.filter((d) => d.estado === 'EN_REVISION').length,
    }),
    [docs],
  );

  const procesosUnicos = useMemo(() => {
    const procs = new Map<string, string>();
    docs.forEach((d) => {
      if (d.proceso) procs.set(d.proceso.codigo ?? '', d.proceso.nombre ?? '');
    });
    return Array.from(procs.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [docs]);

  const displayDocs = useMemo(() => {
    let filtered = [...docs];
    if (filtroTipologia)
      filtered = filtered.filter((d) => d.codigo.startsWith(filtroTipologia + '.'));
    if (filtroEstado) filtered = filtered.filter((d) => d.estado === filtroEstado);
    if (filtroProceso)
      filtered = filtered.filter((d) => (d.proceso?.codigo ?? '') === filtroProceso);
    if (filtroTipoContenido)
      filtered = filtered.filter((d) => (d.tipoContenido ?? 'DIGITAL') === filtroTipoContenido);
    if (busqueda) {
      const q = busqueda.toLowerCase();
      filtered = filtered.filter(
        (d) => d.codigo.toLowerCase().includes(q) || d.titulo.toLowerCase().includes(q),
      );
    }
    filtered.sort((a, b) => {
      let va = '',
        vb = '';
      switch (sortKey) {
        case 'codigo':
          va = a.codigo;
          vb = b.codigo;
          break;
        case 'titulo':
          va = a.titulo;
          vb = b.titulo;
          break;
        case 'tipoDocumental':
          va = a.tipoDocumental ?? '';
          vb = b.tipoDocumental ?? '';
          break;
        case 'estado':
          va = a.estado;
          vb = b.estado;
          break;
        case 'actualizadoEn':
          va = a.actualizadoEn ?? '';
          vb = b.actualizadoEn ?? '';
          break;
      }
      const cmp = va.localeCompare(vb, 'es', { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return filtered;
  }, [
    docs,
    filtroTipologia,
    filtroEstado,
    filtroProceso,
    filtroTipoContenido,
    busqueda,
    sortKey,
    sortDir,
  ]);

  const totalPages = Math.ceil(displayDocs.length / PAGE_SIZE);
  const paginatedDocs = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return displayDocs.slice(start, start + PAGE_SIZE);
  }, [displayDocs, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filtroTipologia, filtroEstado, filtroProceso, filtroTipoContenido, busqueda]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const inputStyle: React.CSSProperties = {
    padding: '8px 14px',
    borderRadius: 8,
    border: '1px solid #d5d5d5',
    fontSize: 14,
    fontFamily: 'inherit',
    backgroundColor: '#fff',
    color: C.text,
    cursor: 'pointer',
    minWidth: 160,
  };

  const rangeStart = (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, displayDocs.length);

  const columns: { label: string; key?: SortKey; width?: number | string }[] = [
    { label: t.docExplorer.codigo ?? 'Código', key: 'codigo', width: 'auto' },
    { label: t.docExplorer.nombre ?? 'Título', key: 'titulo' },
    { label: t.docExplorer.proceso ?? 'Proceso' },
    { label: t.docExplorer.representante ?? 'Representante' },
    { label: t.docExplorer.direccion ?? 'Dirección' },
    { label: t.docExplorer.tipo ?? 'Tipo', key: 'tipoDocumental', width: 80 },
    { label: t.docExplorer.estado ?? 'Estado', key: 'estado', width: 110 },
    { label: 'Ed.', width: 50 },
    { label: t.docExplorer.fecha ?? 'Fecha', key: 'actualizadoEn', width: 100 },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Stats - FIXED, no scroll */}
      <div style={{ flexShrink: 0, padding: '16px 24px 0' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 16,
            marginBottom: 16,
          }}
        >
          <StatCard
            value={stats.total}
            label={t.docExplorer.totalDocumentos ?? 'Total documentos'}
            color={ACCENT}
          />
          <StatCard
            value={stats.vigentes}
            label={t.docExplorer.vigentes ?? 'Vigentes'}
            color="#16A34A"
          />
          <StatCard
            value={stats.borrador}
            label={t.docExplorer.borrador ?? 'Borrador'}
            color="#64748B"
          />
          <StatCard
            value={stats.revision}
            label={t.docExplorer.enRevision ?? 'En revisión'}
            color="#F59E0B"
          />
        </div>
      </div>

      {/* Filters - FIXED, no scroll */}
      <div style={{ flexShrink: 0, padding: '0 24px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <select
            value={filtroTipologia}
            onChange={(e) => setFiltroTipologia(e.target.value as TipologiaFilter)}
            style={inputStyle}
          >
            <option value="">{t.docExplorer.todos}</option>
            <option value="PE">{t.docExplorer.estrategicos}</option>
            <option value="PO">{t.docExplorer.operativos}</option>
            <option value="PS">{t.docExplorer.soporte}</option>
          </select>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            style={inputStyle}
          >
            <option value="">{t.docExplorer.todosEstados ?? 'Todos los estados'}</option>
            <option value="PUBLICADO">{t.docExplorer.vigentes ?? 'Vigente'}</option>
            <option value="BORRADOR">{t.docExplorer.borrador ?? 'Borrador'}</option>
            <option value="EN_REVISION">{t.docExplorer.enRevision ?? 'En revisión'}</option>
            <option value="APROBADO">{t.docExplorer.aprobado ?? 'Aprobado'}</option>
          </select>
          <select
            value={filtroProceso}
            onChange={(e) => setFiltroProceso(e.target.value)}
            style={inputStyle}
          >
            <option value="">{t.docExplorer.todosProcesos ?? 'Todos los procesos'}</option>
            {procesosUnicos.map(([codigo, nombre]) => (
              <option key={codigo} value={codigo}>
                {codigo} {'\u2014'} {nombre}
              </option>
            ))}
          </select>
          <select
            value={filtroTipoContenido}
            onChange={(e) => setFiltroTipoContenido(e.target.value)}
            style={inputStyle}
          >
            <option value="">{t.archivos.todosContenidos}</option>
            <option value="DIGITAL">{t.archivos.digital}</option>
            <option value="ARCHIVO">{t.archivos.archivo}</option>
            <option value="ENLACE_EXTERNO">{t.archivos.enlaceExterno}</option>
          </select>
          <div style={{ position: 'relative', minWidth: 200 }}>
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder={t.docExplorer.buscarPlaceholder ?? 'Buscar documentos...'}
              style={{ ...inputStyle, width: '100%', paddingLeft: 34 }}
            />
            <span
              style={{
                position: 'absolute',
                left: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
              }}
            >
              <Ic d={ICON_PATHS.search} s={14} c="rgba(0,0,0,0.3)" />
            </span>
          </div>
        </div>
      </div>

      {/* Table area - flex:1, scroll only in tbody */}
      <div
        style={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          margin: '0 24px',
          backgroundColor: '#fff',
          borderRadius: 12,
          boxShadow: C.shadow1,
        }}
      >
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <LoadingSpinner text={t.docExplorer.cargandoDocumentos} size="sm" />
          </div>
        ) : error ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#DC2626', fontSize: 14 }}>
            {error}
          </div>
        ) : displayDocs.length === 0 ? (
          <div
            style={{ padding: 48, textAlign: 'center', color: 'rgba(0,0,0,0.38)', fontSize: 14 }}
          >
            {t.docExplorer.noEncontrados}
          </div>
        ) : (
          <>
            {/* Count label - fixed */}
            <div
              style={{
                flexShrink: 0,
                padding: '12px 16px',
                fontSize: 13,
                color: 'rgba(0,0,0,0.38)',
              }}
            >
              {t.docExplorer.mostrando ?? 'Mostrando'} {rangeStart}
              {'\u2013'}
              {rangeEnd} {t.docExplorer.de ?? 'de'} {displayDocs.length}{' '}
              {t.docExplorer.documentos?.toLowerCase() ?? 'documentos'}
            </div>

            {/* Scrollable table with sticky thead */}
            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                  <tr>
                    {columns.map((col) => (
                      <th
                        key={col.label}
                        onClick={col.key ? () => handleSort(col.key!) : undefined}
                        style={{
                          backgroundColor: ACCENT,
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: 11,
                          padding: '12px 16px',
                          textAlign: 'left',
                          whiteSpace: 'nowrap',
                          textTransform: 'uppercase',
                          letterSpacing: '0.8px',
                          width: col.width,
                          cursor: col.key ? 'pointer' : 'default',
                          userSelect: 'none',
                        }}
                      >
                        {col.label}
                        {col.key && sortKey === col.key && (
                          <span style={{ marginLeft: 4, fontSize: 10 }}>
                            {sortDir === 'asc' ? '\u25B2' : '\u25BC'}
                          </span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedDocs.map((doc, idx) => {
                    const roles = doc.proceso?.rolesGobernanza ?? [];
                    const reprRol = roles.find((r) => r.tipoRol === 'REPRESENTANTE_PROCESO');
                    const dirRol = roles.find((r) => r.tipoRol === 'DIRECCION_RESPONSABLE_PROCESO');
                    const reprPersona = reprRol?.puesto?.asignaciones?.[0]?.persona;
                    const reprLabel = reprPersona
                      ? `${reprPersona.nombre ?? ''} ${reprPersona.apellidos ?? ''}`.trim()
                      : (reprRol?.puesto?.titulo ?? '\u2014');
                    const dirLabel = dirRol?.puesto?.titulo ?? '\u2014';
                    return (
                      <tr
                        key={doc.id}
                        onClick={() => {
                          if (doc.tipoContenido === 'ENLACE_EXTERNO') {
                            obtenerUrlArchivo(doc.codigo, 'primary')
                              .then((r) => {
                                window.open(r.data.url, '_blank');
                              })
                              .catch(() => {
                                navigate(`/documento/${doc.codigo}`);
                              });
                          } else {
                            navigate(`/documento/${doc.codigo}`);
                          }
                        }}
                        style={{
                          backgroundColor: idx % 2 === 0 ? '#fff' : '#FAFAFA',
                          cursor: 'pointer',
                          transition: 'background 0.1s',
                          borderBottom: '1px solid #F0F0F0',
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLElement).style.backgroundColor = `${ACCENT}06`;
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLElement).style.backgroundColor =
                            idx % 2 === 0 ? '#fff' : '#FAFAFA';
                        }}
                      >
                        <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                          <span style={{ fontWeight: 600, color: ACCENT, fontSize: 14 }}>
                            {doc.codigo}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: '12px 16px',
                            fontSize: 14,
                            color: C.text,
                            maxWidth: 280,
                          }}
                        >
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1,
                              }}
                            >
                              {doc.titulo}
                            </span>
                            {doc.tipoContenido === 'ARCHIVO' && (
                              <span
                                style={{
                                  display: 'inline-flex',
                                  padding: '1px 6px',
                                  borderRadius: 4,
                                  fontSize: 9,
                                  fontWeight: 700,
                                  letterSpacing: 0.5,
                                  color: '#2563EB',
                                  backgroundColor: '#EFF6FF',
                                  border: '1px solid #2563EB25',
                                  flexShrink: 0,
                                }}
                              >
                                {t.archivos.archivo}
                              </span>
                            )}
                            {doc.tipoContenido === 'ENLACE_EXTERNO' && (
                              <span
                                style={{
                                  display: 'inline-flex',
                                  padding: '1px 6px',
                                  borderRadius: 4,
                                  fontSize: 9,
                                  fontWeight: 700,
                                  letterSpacing: 0.5,
                                  color: '#16A34A',
                                  backgroundColor: '#F0FDF4',
                                  border: '1px solid #16A34A25',
                                  flexShrink: 0,
                                }}
                              >
                                {t.archivos.enlaceExterno}
                              </span>
                            )}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: '12px 16px',
                            fontSize: 13,
                            color: 'rgba(0,0,0,0.54)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {doc.proceso?.nombre ?? '\u2014'}
                        </td>
                        <td
                          style={{
                            padding: '12px 16px',
                            fontSize: 13,
                            color: 'rgba(0,0,0,0.54)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {reprLabel || '\u2014'}
                        </td>
                        <td
                          style={{
                            padding: '12px 16px',
                            fontSize: 13,
                            color: 'rgba(0,0,0,0.54)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {dirLabel}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <DocTypeBadge type={doc.tipoDocumental} />
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <StatusBadge status={doc.estado} small />
                        </td>
                        <td
                          style={{
                            padding: '12px 16px',
                            fontSize: 14,
                            color: 'rgba(0,0,0,0.54)',
                            textAlign: 'center',
                          }}
                        >
                          {String(doc.versionVigente?.numeroEdicion ?? 1).padStart(2, '0')}
                        </td>
                        <td
                          style={{
                            padding: '12px 16px',
                            fontSize: 13,
                            color: 'rgba(0,0,0,0.54)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {formatDate(doc.actualizadoEn)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination - fixed at bottom */}
            <div style={{ flexShrink: 0 }}>
              <Pagination current={currentPage} total={totalPages} onChange={setCurrentPage} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
